import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, Calendar, Users, MapPin, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { captureConnection } from '../../../services/connections';

const INITIAL = { name: '', email: '', phone: '', travelDate: '', travelers: '', message: '' };

const BookingModal = ({ isOpen, onClose, packageInfo }) => {
  const [formData, setFormData] = useState(INITIAL);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset form state whenever the modal closes — `if (!isOpen) return null`
  // (below) returns null but does NOT unmount the component, so without
  // this effect the previous user's values would persist across opens.
  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL);
      setIsSubmitting(false);
      setSubmitError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);
    try {
      await captureConnection('booking', { ...formData, package: packageInfo, form_id: 'package_enquiry' }, {
        page: window.location.pathname,
        component: 'BookingModal',
        reference_id: packageInfo || null,
      });
      alert('Thank you! We\'ll contact you soon.');
      onClose();
    } catch (error) {
      setSubmitError(error.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 p-6">
          <button
            onClick={onClose}
            data-testid="booking-modal-close-button"
            className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center text-white">
            <Sparkles className="w-6 h-6 mr-2" />
            <h2 className="text-xl font-bold">Book Your Adventure</h2>
          </div>
          {packageInfo && (
            <p className="text-white/80 text-sm mt-1">{packageInfo}</p>
          )}
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} data-form-id="package_enquiry" data-testid="booking-modal-form" className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                  data-testid="booking-modal-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                className="pl-10 rounded-xl border-slate-200"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="booking-modal-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className="pl-10 rounded-xl border-slate-200"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="booking-modal-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone"
                  className="pl-10 rounded-xl border-slate-200"
                  required
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Travel Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  data-testid="booking-modal-travel-date"
                  name="travelDate"
                  type="date"
                  value={formData.travelDate}
                  onChange={handleChange}
                  className="pl-10 rounded-xl border-slate-200"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Travelers</label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  name="travelers"
                  value={formData.travelers}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
                  data-testid="booking-modal-travelers"
                  required
                >
                  <option value="">Select travelers</option>
                  {[1,2,3,4,5,6,7,8].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'Person' : 'People'}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message (Optional)</label>
            <textarea
              data-testid="booking-modal-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Any special requests or questions?"
              rows={3}
              className="w-full px-4 py-2 rounded-xl border border-slate-200 resize-none focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
          
          {submitError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="booking-modal-submit-error">
              {submitError}
            </div>
          )}
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="booking-modal-submit-button"
            className="w-full bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white py-6 rounded-full font-semibold shadow-lg shadow-rose-500/30"
          >
            {isSubmitting ? 'Sending Inquiry...' : 'Send Inquiry'}
            <Sparkles className="ml-2 w-5 h-5" />
          </Button>
          
          <p className="text-center text-xs text-slate-500">
            We'll respond within 24 hours. No spam, we promise!
          </p>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
